import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import {
  JiraAvailableProject,
  JiraProjectPermission,
  Role,
  TaskKind,
  User,
} from "../../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../../app/client";
import { useAppSelector } from "../../../../app/hooks";
import UserSpan from "../../users/UserSpan";
import Morselbar from "../../Morselbar";

function JiraProjectAccessDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
  project: JiraAvailableProject;
  permission: JiraProjectPermission;
  callback: () => void;
}) {
  const { open, setOpen, user, project, permission, callback } = props;

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [taskNote, setTaskNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createJiraProjectAccessTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = taskNote || undefined;

    const body: TaskKind = {
      kind: "JiraProjectAccess",
      inner: {
        target: user.employee.id,
        project_id: project.id,
        project_name: project.name,
        permission: permission,
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => callback())
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    taskNote,
    user.employee.id,
    project.id,
    project.name,
    permission,
    client,
    override,
    callback,
  ]);

  return (
    <>
      <Dialog open={open}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              <UserSpan user={user} /> will be granted{" "}
              {permission === JiraProjectPermission.Read ? (
                <Typography component="span" color="success.main">
                  read
                </Typography>
              ) : (
                <>
                  <Typography component="span" color="success.main">
                    read
                  </Typography>{" "}
                  and{" "}
                  <Typography component="span" color="success.main">
                    write
                  </Typography>
                </>
              )}{" "}
              permissions in{" "}
              <Typography component="span" color="secondary">
                {project.name}
              </Typography>
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <br />

          <TextField
            onChange={(e) => setTaskNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={override}
                        onChange={(e) => setOverride(e.target.checked)}
                      />
                    }
                    label="Skip approvals"
                  />
                </>
              )}
            </Box>

            <Button
              onClick={() => createJiraProjectAccessTask()}
              disabled={loading}
              color="success"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}

export default JiraProjectAccessDialog;
